import { useEffect, useState } from 'react'

import type { TillType } from '@/api/teller-types'
import { MoneyInput } from '@/components/money-input'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { Pesewas } from '@/lib/money'

import { TILL_TYPES, tillTypeLabel } from './constants'
import { DualControlNotice, DualControlOutcome } from './dual-control'
import { useCreateTill, useCreateVault } from './mutations'
import { TELLER_PERMISSIONS, useTellerPermissions } from './permissions'
import { TellerActionDialog } from './teller-action-dialog'

/*
 * A till and a vault are branch infrastructure, so both carry `admin.branch.manage` and its
 * countersigning half rather than a `teller.*` code. That is deliberate on the server's side: the
 * catalogue has no `teller.till.manage`, and a permission declared only inside the tellering module
 * could never be granted to anybody.
 */

/** The server's own caps, restated so a refusal is avoided rather than explained. */
const TILL_NUMBER_LIMIT = 16
const NAME_LIMIT = 120

/**
 * Creating a drawer.
 *
 * Dual-controlled, but not uniformly so, and the difference is worth stating: the duplicate check
 * runs *before* the approval gate, so `teller.till_number_taken` comes back immediately while the
 * creation itself waits for a countersignature. An operator who submits a duplicate finds out now;
 * an operator who submits a good one finds out that nothing exists yet.
 */
export function CreateTillDialog({
  branchId,
  open,
  onOpenChange,
}: {
  branchId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const permissions = useTellerPermissions()
  const [tillNumber, setTillNumber] = useState('')
  const [name, setName] = useState('')
  const [tillType, setTillType] = useState<TillType>('Counter')
  const [limit, setLimit] = useState<Pesewas | null>(null)

  const create = useCreateTill()

  useEffect(() => {
    if (!open) {
      setTillNumber('')
      setName('')
      setTillType('Counter')
      setLimit(null)
      create.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <TellerActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Create a till"
      description="A drawer is branch infrastructure: built once when a counter is built, not opened daily."
      confirmLabel="Submit the till"
      isPending={create.isPending}
      disabled={tillNumber.trim() === '' || name.trim() === '' || (limit !== null && limit <= 0)}
      error={create.isError ? create.error : null}
      notice={
        <DualControlNotice
          action="Creating a till"
          approvePermission={TELLER_PERMISSIONS.infrastructureManageApprove}
          canCountersign={permissions.canApproveInfrastructure}
        >
          {' '}
          A duplicate till number is refused straight away, before the gate — so a refusal here means
          the number is taken, not that the countersignature failed.
        </DualControlNotice>
      }
      outcome={
        create.data ? (
          <DualControlOutcome
            result={create.data}
            awaitingDescription="The till has not been created. The request is queued for a countersignature."
            appliedDescription="The till exists and can be floated from the vault."
          />
        ) : undefined
      }
      onConfirm={() => {
        create.mutate({
          branchId,
          tillNumber: tillNumber.trim(),
          name: name.trim(),
          tillType,
          cashHoldingLimitMinor: limit,
          currencyCode: null,
        })
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="till-number">Till number</Label>
          <Input
            id="till-number"
            value={tillNumber}
            maxLength={TILL_NUMBER_LIMIT}
            autoComplete="off"
            onChange={(event) => setTillNumber(event.target.value)}
          />
          <p className="text-xs text-muted">
            Unique at this branch, up to {TILL_NUMBER_LIMIT} characters.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="till-name">Name</Label>
          <Input
            id="till-name"
            value={name}
            maxLength={NAME_LIMIT}
            autoComplete="off"
            onChange={(event) => setName(event.target.value)}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="till-type">Type</Label>
        <Select value={tillType} onValueChange={(value) => setTillType(value as TillType)}>
          <SelectTrigger id="till-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TILL_TYPES.map((value) => (
              <SelectItem key={value} value={value}>
                {tillTypeLabel(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted">
          A counter till is worked in shifts and counted at close. A field till is assigned to an
          officer for a day and is never given a session — so it is never counted or declared here.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="till-limit">Cash holding limit</Label>
        <MoneyInput id="till-limit" value={limit} onChange={setLimit} />
        <p className="text-xs text-muted">
          Optional. The limit is on the drawer rather than on a transaction, and breaching it always
          has the same remedy: bank cash to the vault. It is what makes an officer carrying an
          unreasonable amount across a district visible rather than discovered.
        </p>
      </div>
    </TellerActionDialog>
  )
}

/**
 * Creating a branch vault.
 *
 * One vault per branch per name; a second of the same name is refused immediately with
 * `teller.vault_already_exists`, before the approval gate, exactly as the duplicate till number is.
 */
export function CreateVaultDialog({
  branchId,
  open,
  onOpenChange,
}: {
  branchId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const permissions = useTellerPermissions()
  const [name, setName] = useState('')
  const create = useCreateVault()

  useEffect(() => {
    if (!open) {
      setName('')
      create.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <TellerActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Create a vault"
      description="Every till float comes out of a vault, so a branch without one cannot open a counter."
      confirmLabel="Submit the vault"
      isPending={create.isPending}
      disabled={name.trim() === ''}
      error={create.isError ? create.error : null}
      notice={
        <DualControlNotice
          action="Creating a vault"
          approvePermission={TELLER_PERMISSIONS.infrastructureManageApprove}
          canCountersign={permissions.canApproveInfrastructure}
        />
      }
      outcome={
        create.data ? (
          <DualControlOutcome
            result={create.data}
            awaitingDescription="The vault has not been created. The request is queued for a countersignature."
            appliedDescription="The vault exists and can be funded from the bank."
          />
        ) : undefined
      }
      onConfirm={() => {
        create.mutate({ branchId, name: name.trim(), currencyCode: null })
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="vault-name">Name</Label>
        <Input
          id="vault-name"
          value={name}
          maxLength={NAME_LIMIT}
          autoComplete="off"
          onChange={(event) => setName(event.target.value)}
        />
        <p className="text-xs text-muted">
          Unique at this branch, up to {NAME_LIMIT} characters. Cash reaches it from the bank, and
          leaves it as a float to a till or a lodgement back to the bank.
        </p>
      </div>
    </TellerActionDialog>
  )
}
